import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Pencil } from "lucide-react";

const ET_LABEL = { veicolo: "Veicoli", autista: "Autisti" };

function TipoRow({ t, tipiMezzo, onSaved, onDeleted }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(t);
  const isVeicolo = t.entity_type === "veicolo";

  const toggleMezzo = (m) => setDraft((d) => ({ ...d, tipi_mezzo: d.tipi_mezzo.includes(m) ? d.tipi_mezzo.filter((x) => x !== m) : [...d.tipi_mezzo, m] }));

  const save = async () => {
    try {
      await api.put(`/scadenze-tipi/${t.id}`, draft);
      toast.success("Tipo scadenza aggiornato");
      setEdit(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const del = async () => {
    try {
      await api.delete(`/scadenze-tipi/${t.id}`);
      toast.success("Tipo scadenza eliminato");
      onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`tipo-scad-row-${t.id}`}>
      <td className="px-3 py-2">
        {edit ? <Input className="h-8 text-sm" value={draft.nome} onChange={(e) => setDraft({ ...draft, nome: e.target.value })} data-testid={`tipo-scad-nome-${t.id}`} /> : <span className="font-medium">{t.nome}</span>}
      </td>
      {isVeicolo && (
        <td className="px-3 py-2">
          {edit ? (
            <div className="flex flex-wrap gap-1.5">
              {tipiMezzo.map((m) => (
                <button type="button" key={m} onClick={() => toggleMezzo(m)} data-testid={`tipo-scad-mezzo-${t.id}-${m}`}
                  className={`px-2 py-0.5 rounded-full text-xs border ${draft.tipi_mezzo.includes(m) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"}`}>{m}</button>
              ))}
            </div>
          ) : (t.tipi_mezzo?.length ? t.tipi_mezzo.map((m) => <Badge key={m} variant="outline" className="mr-1 mb-1 text-xs">{m}</Badge>) : <span className="text-xs text-slate-500">Tutti i mezzi</span>)}
        </td>
      )}
      <td className="px-3 py-2 text-center">
        {edit ? <input type="checkbox" checked={!!draft.in_tabella} onChange={(e) => setDraft({ ...draft, in_tabella: e.target.checked })} className="accent-sky-600" /> : (t.in_tabella ? <Check className="h-4 w-4 text-emerald-600 mx-auto" /> : "—")}
      </td>
      <td className="px-3 py-2 text-right text-slate-600">{t.n_scadenze}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {edit ? (
          <>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700" onClick={save} data-testid={`tipo-scad-save-${t.id}`}><Check className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setDraft(t); setEdit(false); }}><X className="h-4 w-4" /></Button>
          </>
        ) : (
          <>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit(true)} data-testid={`tipo-scad-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={del} disabled={t.n_scadenze > 0} title={t.n_scadenze > 0 ? "Usato da scadenze attive" : "Elimina"} data-testid={`tipo-scad-del-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </td>
    </tr>
  );
}

export default function ScadenzeTipiPanel() {
  const [data, setData] = useState({ items: [], tipi_mezzo: [] });
  const [nuovo, setNuovo] = useState({ veicolo: "", autista: "" });

  const load = () => api.get("/scadenze-tipi").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (et) => {
    const nome = nuovo[et].trim();
    if (!nome) return;
    try {
      await api.post("/scadenze-tipi", { entity_type: et, nome, tipi_mezzo: [] });
      toast.success(`Scadenza "${nome}" aggiunta`);
      setNuovo((n) => ({ ...n, [et]: "" }));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3" data-testid="scadenze-tipi-panel">
      <div className="text-xs text-slate-500">Definisci quali scadenze esistono per mezzi e autisti. Per i mezzi puoi limitare una scadenza a certi tipi di veicolo (es. ATP solo per le cisterne): nella scheda veicolo compaiono solo le scadenze pertinenti. "In tabella" mostra la colonna nell'elenco veicoli.</div>
      <Tabs defaultValue="veicolo">
        <TabsList>
          {Object.entries(ET_LABEL).map(([et, l]) => <TabsTrigger key={et} value={et} data-testid={`tipi-tab-${et}`}>{l} ({data.items.filter((t) => t.entity_type === et).length})</TabsTrigger>)}
        </TabsList>
        {Object.keys(ET_LABEL).map((et) => (
          <TabsContent key={et} value={et} className="space-y-3">
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="text-left px-3 py-2">Scadenza</th>{et === "veicolo" && <th className="text-left px-3 py-2">Tipi di mezzo</th>}<th className="px-3 py-2">In tabella</th><th className="text-right px-3 py-2">In uso</th><th></th></tr>
                </thead>
                <tbody>
                  {data.items.filter((t) => t.entity_type === et).map((t) => <TipoRow key={t.id} t={t} tipiMezzo={data.tipi_mezzo} onSaved={load} onDeleted={load} />)}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <Input className="h-9 max-w-sm" placeholder={`Nuova scadenza per ${ET_LABEL[et].toLowerCase()}...`} value={nuovo[et]} onChange={(e) => setNuovo((n) => ({ ...n, [et]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && add(et)} data-testid={`tipi-nuovo-${et}`} />
              <Button size="sm" className="h-9" onClick={() => add(et)} data-testid={`tipi-add-${et}`}><Plus className="h-4 w-4 mr-1.5" />Aggiungi</Button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
